import { IF, IS, UINativeScrollView, UIView } from "uicore-ts"


type ColumnCellSizing =
    | { type: "intrinsic" }
    | { type: "fixed"; height: number }
    | { type: "fill"; weight: number }
    | { type: "scrollFill"; weight: number }


export class ColumnCellDescriptor {
    
    readonly cell: UIView
    readonly sizing: ColumnCellSizing
    
    private constructor(cell: UIView, sizing: ColumnCellSizing) {
        this.cell = cell
        this.sizing = sizing
    }
    
    /** The cell always sizes itself to its own intrinsic content height.
     *  Use this for headers, labels, toolbars — anything whose height is
     *  determined by its content and should never be stretched or clipped. */
    static intrinsic(cell: UIView): ColumnCellDescriptor {
        return new ColumnCellDescriptor(cell, { type: "intrinsic" })
    }
    
    /** The cell is given an exact pixel height regardless of its content.
     *  Use this for dividers, separators, or any element with a known
     *  constant height. */
    static fixed(cell: UIView, height: number): ColumnCellDescriptor {
        return new ColumnCellDescriptor(cell, { type: "fixed", height })
    }
    
    /** The cell expands to consume the space left over after all intrinsic
     *  and fixed cells have been measured.
     *
     *  If the ColumnView is taller than its total content, the surplus is
     *  distributed among fill cells proportionally by weight. For example,
     *  weights of 1 and 2 give the first cell one third and the second cell
     *  two thirds of the surplus. Weight defaults to 1.
     *
     *  A fill cell never shrinks below its own intrinsic content height —
     *  if the ColumnView is shorter than its content the cell stays at its
     *  natural size and the ColumnView's own scroll handles the overflow.
     *
     *  Use this when the cell itself does not scroll and you want the
     *  ColumnView to be the scroll container. If the cell manages its own
     *  scrolling (e.g. a UITableView), use scrollFill instead. */
    static fill(cell: UIView, weight: number = 1): ColumnCellDescriptor {
        return new ColumnCellDescriptor(cell, { type: "fill", weight })
    }
    
    /** Like fill, but the cell is always given exactly the available space —
     *  it can receive a frame smaller than its intrinsic content height.
     *
     *  Use this when the cell manages its own internal scrolling (e.g. a
     *  UITableView). The cell is sized to the viewport, not to its content,
     *  so the cell's scroll mechanism handles overflow rather than the
     *  ColumnView's.
     *
     *  During virtual layout passes the cell still reports its intrinsic
     *  content height upward so that parent views can measure the total
     *  content size correctly. */
    static scrollFill(cell: UIView, weight: number = 1): ColumnCellDescriptor {
        return new ColumnCellDescriptor(cell, { type: "scrollFill", weight })
    }
    
}


export class ColumnView extends UINativeScrollView {
    
    _descriptors: ColumnCellDescriptor[] = []
    
    padding = 0
    minimumContentWidth = 0
    
    constructor(elementID?: string) {
        super(elementID)
        this.scrollsX = false
        this.scrollsY = true
    }
    
    
    // MARK: - Descriptors / Cells
    
    /** The full sizing configuration for all cells.
     *
     *  Set this when you need explicit control over how individual cells are
     *  sized. Each descriptor pairs a view with a sizing rule created via the
     *  factory methods on ColumnCellDescriptor:
     *
     *      columnView.descriptors = [
     *          ColumnCellDescriptor.intrinsic(this.headerView),
     *          ColumnCellDescriptor.fixed(this.divider, 1),
     *          ColumnCellDescriptor.scrollFill(this.tableView),
     *      ]
     *
     *  Setting descriptors replaces the current cell list entirely.
     *  Views that are no longer present are removed from the view tree. */
    get descriptors(): ColumnCellDescriptor[] {
        return this._descriptors
    }
    
    set descriptors(descriptors: ColumnCellDescriptor[]) {
        
        const previousCells = this._descriptors.map(descriptor => descriptor.cell)
        const nextCells = descriptors.map(descriptor => descriptor.cell)
        
        previousCells.forEach(cell => {
            if (!nextCells.contains(cell)) {
                cell.removeFromSuperview()
            }
        })
        
        nextCells.forEach(cell => {
            if (!IS(cell.superview)) {
                this.addSubview(cell)
            }
        })
        
        this._descriptors = descriptors
        this.setNeedsLayout()
        
    }
    
    /** Convenience accessor that returns the view from each descriptor in order. */
    get cells(): UIView[] {
        return this._descriptors.map(descriptor => descriptor.cell)
    }
    
    /** Convenience setter for when all cells should size themselves
     *  intrinsically. Equivalent to setting descriptors with every cell
     *  wrapped in ColumnCellDescriptor.intrinsic():
     *
     *      columnView.cells = [this.headerView, this.tableView]
     *
     *  If you need any cell to have a fixed, fill, or scrollFill sizing,
     *  use the descriptors setter instead. */
    set cells(cells: UIView[]) {
        this.descriptors = cells.map(cell => ColumnCellDescriptor.intrinsic(cell))
    }
    
    
    // MARK: - Layout
    
    
    override layoutSubviews() {
        
        super.layoutSubviews()
        
        const bounds = this.contentBounds
        const layoutBounds = bounds.rectangleWithWidth([bounds.width, this.minimumContentWidth].max())
        
        if (!this._descriptors.length) {
            return
        }
        
        const hasFillCells = this._descriptors.anyMatch(
            descriptor => descriptor.sizing.type === "fill" || descriptor.sizing.type === "scrollFill"
        )
        
        if (!hasFillCells) {
            
            // All cells are intrinsic or fixed — simple column distribution
            layoutBounds.framesByDistributingViewsAsColumn(
                this._descriptors.map(descriptor => descriptor.cell),
                this.padding,
                this._descriptors.map(descriptor =>
                    IF(descriptor.sizing.type === "fixed")(() =>
                        (descriptor.sizing as { type: "fixed"; height: number }).height as number | UIView
                    ).ELSE(() =>
                        descriptor.cell as number | UIView
                    )
                )
            )
            
            return
            
        }
        
        // One or more fill/scrollFill cells present.
        //
        // First pass: measure the height consumed by all intrinsic and fixed
        // cells (plus padding gaps). This tells us how much space remains for
        // fill and scrollFill cells to share.
        
        let consumedHeight = (this._descriptors.length - 1) * this.padding
        
        this._descriptors.forEach(descriptor => {
            
            const sizing = descriptor.sizing
            
            if (sizing.type === "fixed") {
                consumedHeight += sizing.height
            }
            else if (sizing.type === "intrinsic") {
                consumedHeight += descriptor.cell.intrinsicContentHeight(layoutBounds.width)
            }
            
        })
        
        const availableForFill = [0, layoutBounds.height - consumedHeight].max()
        
        // Collect the combined weight of all fill-type cells (both fill and
        // scrollFill share the same weight pool and surplus distribution logic).
        const totalFillWeight = this._descriptors.reduce((sum, descriptor) => {
            
            const type = descriptor.sizing.type
            if (type !== "fill" && type !== "scrollFill") {
                return sum
            }
            
            return sum + (descriptor.sizing as { type: "fill" | "scrollFill"; weight: number }).weight
            
        }, 0)
        
        // Second pass: assign a frame to every cell.
        let currentFrame = layoutBounds.rectangleWithHeight(0)
        
        this._descriptors.forEach((descriptor, index) => {
            
            const sizing = descriptor.sizing
            const rowPadding = index === 0 ? 0 : this.padding
            
            let height: number
            
            if (sizing.type === "fixed") {
                height = sizing.height
            }
            else if (sizing.type === "fill") {
                // fill: distribute surplus proportionally, but never let the
                // cell shrink below its own intrinsic height. When the
                // ColumnView is shorter than its total content the cell keeps
                // its natural size and the ColumnView's own scroll handles
                // the overflow.
                const fillWeight = (sizing as { type: "fill"; weight: number }).weight
                const fillHeight = totalFillWeight > 0
                                   ? (fillWeight / totalFillWeight) * availableForFill
                                   : 0
                height = [fillHeight, descriptor.cell.intrinsicContentHeight(bounds.width)].max()
            }
            else if (sizing.type === "scrollFill") {
                // scrollFill: distribute surplus proportionally, exactly like
                // fill, but with no intrinsic-height floor.
                //
                // The cell receives exactly the available space, even if that
                // is less than its intrinsic content height. The cell is
                // responsible for scrolling its own overflow internally (e.g.
                // UITableView).
                //
                // During virtual layout passes isVirtualLayouting is true and
                // availableForFill is meaningless (the parent is just asking
                // "how tall are you?"). In that case we return the intrinsic
                // content height so parent views can measure the total content
                // size correctly.
                const fillWeight = (sizing as { type: "scrollFill"; weight: number }).weight
                const proportionalHeight = totalFillWeight > 0
                                           ? (fillWeight / totalFillWeight) * availableForFill
                                           : 0
                height = IF(this.isVirtualLayouting)(() =>
                    descriptor.cell.intrinsicContentHeight(layoutBounds.width)
                ).ELSE(() =>
                    proportionalHeight
                )
            }
            else {
                height = descriptor.cell.intrinsicContentHeight(layoutBounds.width)
            }
            
            currentFrame = currentFrame.rectangleForNextRow(rowPadding, height)
            descriptor.cell.frame = currentFrame
            
        })
        
    }
    
    override intrinsicContentHeight(constrainingWidth?: number): number {
        
        if (!this._descriptors.length) {
            return 0
        }
        
        const width = [constrainingWidth ?? this.bounds.width, this.minimumContentWidth].max()
        
        // Sum up the contribution of every cell without touching any frames.
        // - fixed: known constant
        // - intrinsic: ask the cell directly
        // - fill / scrollFill: ask the cell for its intrinsic height as the
        //   floor (fill never shrinks below it; scrollFill reports it upward
        //   during virtual passes so parents can measure total content size)
        let total = (this._descriptors.length - 1) * this.padding
        
        for (const descriptor of this._descriptors) {
            const sizing = descriptor.sizing
            if (sizing.type === "fixed") {
                total += sizing.height
            }
            else {
                total += descriptor.cell.intrinsicContentHeight(width)
            }
        }
        
        return total
        
    }
    
}
